import { useRef, useState } from 'react';
import { useAssistantRuntime, useAui, useThread } from '@assistant-ui/react';
import {
  FINANCIAL_STATEMENT_OFFER_COPY,
  type FinancialStatementOffer,
} from '@upright/common/mcp-payloads/financial-statement-offer';
import { Button } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Heading, Text } from '@upright/ui/common/generic/components/typography';
import { validateAttachmentFile } from './attachment-validation';
import { buildUploadErrorMessage } from './upload-error-message';

export function FinancialStatementOfferCard({ data }: { data: FinancialStatementOffer }) {
  const runtime = useAssistantRuntime();
  const aui = useAui();
  const running = useThread(state => state.isRunning);
  const fileInput = useRef<HTMLInputElement>(null);
  const [chosen, setChosen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const disabled = chosen || running || uploading;
  const send = (message: string) => {
    setChosen(true);
    runtime.thread.append(message);
  };
  const upload = async (file: File) => {
    const rejection = validateAttachmentFile(file);
    if (rejection) {
      setError(buildUploadErrorMessage(rejection));
      return;
    }
    setUploading(true);
    setError(undefined);
    try {
      const existing = runtime.thread.composer.getState();
      const hadDraft = existing.text.trim().length > 0 || existing.attachments.length > 0;
      await aui.composer().addAttachment(file);
      const text = runtime.thread.composer.getState().text;
      if (!text.trim()) {
        aui
          .composer()
          .setText(`Read the attached statements into a financial statement draft for ${data.companyName}.`);
      }
      if (hadDraft || text.trim()) {
        setNotice('Statements attached. Send your message below to continue.');
      } else {
        aui.composer().send();
      }
      setChosen(true);
    } catch {
      setError('The file could not be attached. Try uploading it again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <DataCard>
      <Stack gap="md">
        <Heading level={3} size="lg">
          Add financial statements
        </Heading>
        <Text size="sm" color="gray-600">
          {data.companyName}
        </Text>
        <Text>{FINANCIAL_STATEMENT_OFFER_COPY}</Text>
        <input
          ref={fileInput}
          type="file"
          hidden
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
            event.target.value = '';
          }}
        />
        {error && (
          <div role="alert">
            <Text>{error}</Text>
          </div>
        )}
        {notice && (
          <div role="status">
            <Text>{notice}</Text>
          </div>
        )}
        <Stack row gap="sm" wrap>
          <Button
            size="small"
            variant="outlined"
            isDisabled={disabled}
            onPress={() => {
              send(
                data.beforeFirstResults
                  ? `Skip financial statements for ${data.companyName} and compute results.`
                  : `Skip financial statements for ${data.companyName} for now.`,
              );
            }}
          >
            {data.beforeFirstResults ? 'Skip, compute results' : 'Skip for now'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            isDisabled={disabled}
            onPress={() => {
              send(`Let me fill in financial statements manually for ${data.companyName}. Open an empty draft.`);
            }}
          >
            Fill in manually
          </Button>
          <Button
            size="small"
            variant="contained"
            isDisabled={disabled}
            onPress={() => {
              if (data.attachedReportName)
                send(
                  `Read the attached statements in ${data.attachedReportName} into a financial statement draft for ${data.companyName}.`,
                );
              else fileInput.current?.click();
            }}
          >
            {uploading ? 'Attaching…' : data.attachedReportName ? 'Read attached statements' : 'Upload statements'}
          </Button>
        </Stack>
      </Stack>
    </DataCard>
  );
}
